import React, { memo, useMemo, useState, useCallback } from 'react'
import { Route, useParams, useHistory } from 'react-router-dom'
import compact from 'lodash/compact'
import TopNav from 'transfix-ui/components/TopNav'
import { makeStyles } from 'transfix-ui/core/styles'
import { useDebouncedCallback } from 'use-debounce/lib'
import { LoadDetailsModal } from 'apps/fleet-planner/components/load-details/LoadDetailsModal'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { Container } from 'components/layout'
import MissingInfoFilters from './components/MissingInfoFilters'
import ShipmentsNeedingAttentionPagination from './components/ShipmentsNeedingAttentionPagination'
import ShipmentsNeedingAttentionRows from './components/ShipmentsNeedingAttentionRows'
import { SHIPMENTS_PER_PAGE } from './constants'
import {
  ILoadsNeedingAttentionQueryVariables,
  useLoadsNeedingAttentionQuery,
} from './graphql/LoadsNeedingAttention'
import { IShipmentMissingInfoFragment } from './graphql/ShipmentMissingInfoFragment'
import { IMissingInfoFilters } from './types'
import { getNeedsAttentionQueryFilters } from './utils/getNeedsAttentionQueryFilters'

const useShipmentsNeedingAttentionPageStyles = makeStyles(theme => ({
  pageBody: {
    padding: `${theme.spacing(3)} 0`,

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
  },
  filters: {
    marginBottom: theme.spacing(3.25),
  },
  rows: {
    padding: `0 ${theme.spacing(2)}`,

    [theme.breakpoints.up('md')]: {
      padding: `0`,
    },
  },
}))

export interface IShipmentsNeedingAttentionPageProps {}

const ShipmentsNeedingAttentionPage = memo<IShipmentsNeedingAttentionPageProps>(() => {
  const classes = useShipmentsNeedingAttentionPageStyles()
  const history = useHistory()
  const { trackEvent } = useFMSAnalytics()

  const [filters, setFilters] = useState<IMissingInfoFilters[]>([IMissingInfoFilters.any])
  const [page, setPage] = useState<number>(1)

  const variables: ILoadsNeedingAttentionQueryVariables = useMemo(() => {
    if (filters.includes(IMissingInfoFilters.any)) {
      return { first: SHIPMENTS_PER_PAGE, before: null, after: null }
    }
    return {
      first: SHIPMENTS_PER_PAGE,
      before: null,
      after: null,
      filters: getNeedsAttentionQueryFilters(filters),
    }
  }, [filters])

  const { data, loading, refetch } = useLoadsNeedingAttentionQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables,
  })
  const { shipmentsMissingInfo } = data || {}
  const { nodes, pageInfo, totalCount = 0 } = shipmentsMissingInfo || {}
  const { endCursor, startCursor } = pageInfo || {}
  const shipments = compact<IShipmentMissingInfoFragment>(nodes)

  const { shipmentId } = useParams<{
    shipmentId: string
  }>()

  const backHandler = useCallback(() => {
    history.replace('/home')
  }, [history])

  const closeModalHandler = useCallback(() => {
    history.replace(`/loads-needing-attention`)
    refetch()
  }, [history, refetch])

  const filterChangeHandler = useCallback(
    (filters: IMissingInfoFilters[]) => {
      trackEvent('Loads Needing Attention', 'FILTERED_LOADS_NEEDING_ATTENTION', { filters })
      setPage(1)
      setFilters(filters)
    },
    [trackEvent]
  )
  const [debounceFilterChangeHandler] = useDebouncedCallback(filterChangeHandler, 250)

  const pageChangeHandler = useCallback(
    (nextPage: number) => {
      if (nextPage > page) {
        refetch({ ...variables, before: null, after: endCursor })
      } else if (nextPage < page) {
        refetch({ ...variables, before: startCursor, after: null })
      }
      setPage(nextPage)
    },
    [endCursor, page, refetch, startCursor, variables]
  )

  return (
    <div>
      <TopNav
        title='Loads Needing Attention'
        showMenu={false}
        showBackButton
        onPressBackButton={backHandler}
      />
      <Container hidePadding>
        <div className={classes.pageBody}>
          <div className={classes.filters}>
            <MissingInfoFilters
              selectedFilters={filters}
              onFilterChange={debounceFilterChangeHandler}
            />
          </div>
          <div className={classes.rows}>
            <ShipmentsNeedingAttentionRows shipments={shipments} loading={loading} />
          </div>
          <ShipmentsNeedingAttentionPagination
            currentPage={page}
            totalCount={totalCount}
            onPageChange={pageChangeHandler}
          />
        </div>
      </Container>
      <Route exact path='/loads-needing-attention/:shipmentId/details/:action?'>
        <LoadDetailsModal
          id={shipmentId}
          onClose={closeModalHandler}
          path='/loads-needing-attention/:shipmentId/details'
        />
      </Route>
    </div>
  )
})

export default ShipmentsNeedingAttentionPage
